import React, { useEffect, useState, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { patchDocument } from "../../../api/elearning/documents/documents";
import { getSubjectsLookup } from "../../../api/elearning/subjects/subjectInfo";
import { getAllCoursesNoPaging } from "../../../api/elearning/courses/courses";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";

const DROPDOWN_Z = 200;

const documentTypes = [
  { label: "Giáo trình", value: "giao_trinh" },
  { label: "Bài giảng", value: "bai_giang" },
  { label: "Bài tập", value: "bai_tap" },
  { label: "Video bài học", value: "video" },
  { label: "Đề kiểm tra", value: "de_thi" },
  { label: "Khác", value: "khac" },
];

function normalizeDocType(type) {
  const t = String(type || "").toLowerCase().trim();
  if (t === "baigiang" || t === "bai_giang") return "bai_giang";
  if (t === "giaotrinh" || t === "giao_trinh") return "giao_trinh";
  if (t === "baitap" || t === "bai_tap") return "bai_tap";
  if (t === "dethi" || t === "de_thi") return "de_thi";
  if (t === "video") return "video";
  return t || "khac";
}

function FieldLabel({ children, required, hint }) {
  return (
    <div className="mb-1.5 flex items-center justify-between gap-2 text-xs font-bold text-slate-700">
      <span>
        {children} {required && <span className="text-red-500">*</span>}
      </span>
      {hint && <span className="font-normal text-slate-400">{hint}</span>}
    </div>
  );
}

const CustomSelect = ({
  label,
  required,
  hint,
  value,
  onChange,
  options = [],
  placeholder,
  loading,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuRect, setMenuRect] = useState(null);

  const syncMenuPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const desiredMax = 260;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
    const maxListH = Math.min(
      desiredMax,
      openUp ? spaceAbove - 12 : spaceBelow - 12
    );
    const left = Math.max(
      margin,
      Math.min(r.left, window.innerWidth - r.width - margin)
    );
    setMenuRect({
      left,
      width: r.width,
      maxHeight: Math.max(100, maxListH),
      ...(openUp
        ? { bottom: window.innerHeight - r.top + margin }
        : { top: r.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuRect(null);
      return;
    }
    syncMenuPosition();
    const onScroll = () => syncMenuPosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [isOpen, options.length, syncMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointer = (e) => {
      const t = triggerRef.current;
      const m = menuRef.current;
      if (t?.contains(e.target) || m?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [isOpen]);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  const menuPortal =
    isOpen &&
    menuRect &&
    typeof document !== "undefined" &&
    createPortal(
      <motion.div
        ref={menuRef}
        role="listbox"
        initial={{ opacity: 0, y: menuRect.top != null ? -4 : 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.1 }}
        className="fixed overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl ring-1 ring-black/5"
        style={{
          left: menuRect.left,
          width: menuRect.width,
          zIndex: DROPDOWN_Z,
          ...(menuRect.top != null ? { top: menuRect.top } : {}),
          ...(menuRect.bottom != null ? { bottom: menuRect.bottom } : {}),
          maxHeight: menuRect.maxHeight,
        }}
      >
        <div
          className="custom-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
          style={{ maxHeight: menuRect.maxHeight }}
        >
          {options.length === 0 ? (
            <div className="px-3 py-2.5 text-center text-sm text-slate-400">
              Không có dữ liệu
            </div>
          ) : (
            options.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={String(value) === String(opt.value)}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                  String(value) === String(opt.value)
                    ? "bg-red-50 font-semibold text-red-700"
                    : "font-medium text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span className="truncate">{opt.label}</span>
                {String(value) === String(opt.value) ? (
                  <CheckCircle2 size={15} className="shrink-0 text-red-600" />
                ) : null}
              </button>
            ))
          )}
        </div>
      </motion.div>,
      document.body
    );

  return (
    <div>
      {label && (
        <FieldLabel required={required} hint={hint}>
          {label}
        </FieldLabel>
      )}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled || loading}
        onClick={() => !disabled && !loading && setIsOpen((prev) => !prev)}
        className={`flex h-11 w-full items-center justify-between gap-2 rounded-2xl border px-3.5 text-left text-sm font-semibold transition ${
          disabled
            ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300"
            : isOpen
              ? "border-red-500 bg-white text-slate-800 shadow-sm ring-2 ring-red-50"
              : "border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50/60"
        }`}
      >
        <span
          className={`truncate ${
            !selectedOption ? "font-normal text-slate-400" : ""
          }`}
        >
          {loading
            ? "Đang tải…"
            : selectedOption
              ? selectedOption.label
              : placeholder || "Chọn…"}
        </span>
        <svg
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {menuPortal}
    </div>
  );
};

const DocumentEditModal = ({ isOpen, document: doc, onClose, onSaved }) => {
  const [fileName, setFileName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [subject, setSubject] = useState("");
  const [courseId, setCourseId] = useState("");
  const [docType, setDocType] = useState("");
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setSubjectsLoading(true);
    getSubjectsLookup()
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res) ? res : res?.items || [];
        setSubjects(items);
      })
      .catch(() => {
        if (!cancelled) setSubjects([]);
      })
      .finally(() => {
        if (!cancelled) setSubjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !subject) {
      setCourses([]);
      return;
    }
    const sub = subjects.find((s) => s.code === subject);
    if (!sub?.id) {
      setCourses([]);
      return;
    }
    let cancelled = false;
    setCoursesLoading(true);
    getAllCoursesNoPaging({ subject_id: sub.id, is_active: "true" })
      .then((data) => {
        if (cancelled) return;
        const raw = Array.isArray(data) ? data : data?.records ?? data?.items ?? [];
        setCourses(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        if (!cancelled) setCourses([]);
      })
      .finally(() => {
        if (!cancelled) setCoursesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, subject, subjects]);

  useEffect(() => {
    if (!isOpen || !doc) return;
    setFileName(doc.file_name || "");
    setDescription(doc.description || "");
    setIsActive(doc.is_active !== false);
    setSubject(doc.subject || "");
    setCourseId(doc.course_id || "");
    setDocType(normalizeDocType(doc.type));
  }, [isOpen, doc]);

  const handleSave = async () => {
    if (!doc?.id) return;
    if (!fileName.trim()) return showErrorToast("Vui lòng nhập tên tài liệu.");
    if (!subject) return showErrorToast("Vui lòng chọn môn học.");
    if (!docType) return showErrorToast("Vui lòng chọn phân loại tài liệu.");

    setLoading(true);
    try {
      await patchDocument(doc.id, {
        file_name: fileName.trim(),
        description: description.trim(),
        is_active: isActive,
        subject,
        course_id: courseId || null,
        type: docType,
      });
      showSuccessToast("Đã cập nhật tài liệu học tập.");
      onSaved?.();
      onClose?.();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || err.message || "Không thể cập nhật tài liệu."
      );
    } finally {
      setLoading(false);
    }
  };

  const courseOptions = [
    { label: "Dùng chung môn học", value: "" },
    ...courses.map((c) => ({
      label: c.name,
      value: c.id,
    })),
  ];

  return (
    <AnimatePresence>
      {isOpen && doc && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="bg-white w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden pointer-events-auto border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-red-600 via-rose-600 to-red-700 text-white">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-white shadow-inner">
                    <FileText size={20} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-black uppercase tracking-tight truncate">
                      Sửa tài liệu học tập
                    </h2>
                    <p className="text-xs text-rose-100 font-mono truncate">
                      Mã: {doc.code || doc.id}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl p-1.5 hover:bg-white/15 text-white/90 hover:text-white transition"
                  aria-label="Đóng"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Form Body */}
              <div className="p-6 space-y-4 max-h-[72vh] overflow-y-auto text-left">
                {/* Tên hiển thị */}
                <div>
                  <FieldLabel required>Tên hiển thị tài liệu</FieldLabel>
                  <input
                    type="text"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder="VD: Bài giảng số 01..."
                    className="h-11 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none transition focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-50"
                  />
                </div>

                {/* Mô tả */}
                <div>
                  <FieldLabel hint="Không bắt buộc">Mô tả tóm tắt</FieldLabel>
                  <textarea
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Nhập thông tin giới thiệu hoặc lưu ý sử dụng của tài liệu..."
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-50"
                  />
                </div>

                {/* Môn học */}
                <CustomSelect
                  label="Môn học"
                  required
                  placeholder="Chọn môn học"
                  loading={subjectsLoading}
                  disabled={loading}
                  value={subject}
                  options={subjects.map((s) => ({
                    label: s.name,
                    value: s.code,
                  }))}
                  onChange={(val) => {
                    setSubject(val);
                    setCourseId("");
                  }}
                />

                {/* 1 HÀNG 2 Ô CHỌN: PHÂN LOẠI & KHÓA HỌC */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <CustomSelect
                    label="Phân loại"
                    required
                    placeholder="Chọn phân loại tài liệu"
                    disabled={loading}
                    value={docType}
                    options={documentTypes}
                    onChange={(val) => setDocType(val)}
                  />

                  <CustomSelect
                    label="Khóa học"
                    hint="Không bắt buộc"
                    placeholder={
                      subject ? "Dùng chung môn học" : "Chọn môn học trước"
                    }
                    loading={coursesLoading}
                    disabled={loading || !subject}
                    value={courseId}
                    options={courseOptions}
                    onChange={(val) => setCourseId(val || "")}
                  />
                </div>

                {/* Trạng thái sử dụng */}
                <div className="pt-2">
                  <label className="flex items-center gap-2.5 cursor-pointer text-sm font-medium text-slate-700 select-none">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span>Đang sử dụng (hiển thị khi gán bài học)</span>
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-5 py-2.5 rounded-xl font-bold text-xs text-gray-600 hover:bg-gray-200 transition"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-500 hover:to-red-600 shadow-md shadow-red-200 flex items-center gap-2 disabled:opacity-50 transition"
                >
                  {loading && <Loader2 className="animate-spin" size={16} />}
                  Lưu thay đổi
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DocumentEditModal;
